<template>
  <v-tabs
    v-model="localSelected"
    bg-color="primary"
    slider-color="#378CDA"
    align-tabs="start"
  >
    <v-tab
      v-for="dt in sortedTwins"
      :key="dt.id"
      :value="dt.id"
      :class="{ 'dt-tab--active': localSelected === dt.id }"
    >
      <span
        class="text-subtitle-1 text-white"
        :class="localSelected === dt.id ? 'font-weight-bold' : ''"
      >
        {{ siteSystemLabels[dt.siteSystemId] ?? `Digital Twin #${dt.displayOrder}` }}
      </span>
    </v-tab>
  </v-tabs>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { IDigitalTwin } from '../types';

const props = defineProps<{
  digitalTwins: IDigitalTwin[];
  modelValue: number | null;
  siteSystemLabels: Record<number, string>;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: number];
}>();

const localSelected = computed({
  get: () => props.modelValue ?? props.digitalTwins[0]?.id ?? null,
  set: (val: number) => emit('update:modelValue', val),
});

const sortedTwins = computed<IDigitalTwin[]>(() =>
  [...props.digitalTwins].sort((a, b) => a.displayOrder - b.displayOrder)
);
</script>

<style lang="scss" scoped>
// Dim inactive tabs so the selected one clearly stands out against the
// primary-colored toolbar. Hover restores full emphasis for affordance.
.v-tab {
  opacity: 0.6;
  transition: opacity 0.2s ease, background-color 0.2s ease;

  &:hover {
    opacity: 0.85;
  }
}

// Selected tab: full emphasis + a subtle translucent pill.
.dt-tab--active {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.16);
}

// A thicker underline slider reinforces which tab is active.
:deep(.v-tab__slider) {
  height: 3px;
}
</style>
